/**
 * SeenItAtBadge ✦ iter230 — premium "We've been here" badge.
 *
 * Drop on any product page passing the package slug. Pulls live
 * /api/products/{slug}/stats and renders:
 *
 *   Seen it at 42 real weddings across 18 venues ✦
 *
 * Clicking deep-links to /venues?package=<slug> which pre-applies
 * the package filter on the interactive map.
 *
 * Auto-hides when no completed events exist for the package yet.
 *
 * Props:
 *   package         (required) package slug, e.g. "blossom-booth"
 *   variant         "cream" | "dark"  (default cream)
 *   eventLabel      copy override — e.g. "real weddings" / "events" / "schools"
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';

const PALETTES = {
  cream: {
    bg: '#FFFCF6',
    border: 'rgba(156,124,46,0.32)',
    primary: '#4B371E',
    accent: '#9C7C2E',
    dim: '#7A5C3E',
    shadow: '0 12px 32px rgba(75,55,30,0.10)',
  },
  dark: {
    bg: 'rgba(20,12,8,0.85)',
    border: 'rgba(212,170,82,0.32)',
    primary: '#FBEED0',
    accent: '#D4AF5E',
    dim: 'rgba(251,238,208,0.66)',
    shadow: '0 14px 36px rgba(0,0,0,0.42)',
  },
};

const SeenItAtBadge = ({ package: pkg, variant = 'cream', eventLabel = 'real events' }) => {
  const [stats, setStats] = useState(null);
  const P = PALETTES[variant] || PALETTES.cream;

  useEffect(() => {
    if (!pkg) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/products/${pkg}/stats`);
        if (!cancelled) setStats(r.data || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [pkg]);

  if (!stats || !stats.events_count) return null;

  const productLabel = stats.package_label || pkg.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  return (
    <Link
      to={`/venues?package=${pkg}`}
      data-testid={`seen-it-at-${pkg}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 16,
        textDecoration: 'none', padding: '16px 20px',
        background: P.bg, border: `1px solid ${P.border}`,
        borderRadius: 14, boxShadow: P.shadow,
        fontFamily: SERIF, color: P.primary,
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        maxWidth: 580,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = variant === 'dark'
          ? '0 22px 50px rgba(0,0,0,0.55)'
          : '0 18px 44px rgba(75,55,30,0.18)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = P.shadow;
      }}
    >
      <div style={{
        fontFamily: SERIF, fontSize: 26, color: P.accent,
        lineHeight: 1, minWidth: 28,
      }}>✦</div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontFamily: SERIF, fontWeight: 500, fontSize: 15.5,
          color: P.primary, letterSpacing: '-0.005em', marginBottom: 4,
        }}>{productLabel}</div>
        <div style={{
          fontFamily: SERIF, fontSize: 13.5, color: P.dim, lineHeight: 1.5,
        }}>
          Seen at <strong style={{ color: P.primary }}>{stats.events_count}</strong> {eventLabel}
          {stats.venues_count > 0 && (
            <> across <strong style={{ color: P.primary }}>{stats.venues_count}</strong> venue{stats.venues_count === 1 ? '' : 's'}</>
          )}
          {stats.average_rating > 0 && (
            <span> · {stats.average_rating}★</span>
          )}
        </div>
      </div>
      <div style={{
        fontFamily: SERIF, fontSize: 12, fontStyle: 'italic',
        color: P.accent, letterSpacing: '0.04em', whiteSpace: 'nowrap',
      }}>Filter map →</div>
    </Link>
  );
};

export default SeenItAtBadge;
